<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import ChatInput from './ChatInput.svelte';
  import MarkdownContent from './MarkdownContent.svelte';
  import DocumentPanel from './DocumentPanel.svelte';
  import SourcesPanel from './SourcesPanel.svelte';
  import DropZone from '../common/DropZone.svelte';
  import { getPalette } from '../../theme.svelte';
  import {
    getMessages,
    addUserMessage,
    addUserAttachmentMessage,
    updateLatestAudioTranscription,
    addAssistantMessage,
    addSources,
    setStatusMessage,
    removeStatusMessages,
    touchChat,
    loadChatMessages,
    reloadChatMessages,
    recoverLatestAssistantMessage,
    consumePendingInitialMessage,
    consumeScrollTarget,
    scrollTargetByChat,
    markChatUnread,
    markChatRead,
    busyByChat,
    setChatBusy,
    deepWaitByChat,
    setDeepWait,
    clearDeepWait,
    loadAIPreferences,
    loadChatPreferences,
    setChatReasoningEffort,
    awaitPendingEffortWrite,
    getEffectiveEffort,
    getEffortOptions,
    type Attachment,
    uploadChatDocumentBatch,
    waitForChatDocuments,
    MAX_CHAT_MESSAGE_DOCUMENTS,
  } from '../../chats.svelte';
  import {
    initGlobalSocket,
    subscribeMessages,
    subscribeClose,
    subscribeError,
    subscribeSources,
    sendChatMessage,
    sendInterrupt,
    pollPending,
    sendAudioMessage,
    sendImageMessage,
    sendDocumentMessage,
    sendDocumentBatch,
  } from '../ws';
  import { navigate, getRoute } from '../../route.svelte';
  import { showGlobalError } from '../../errorBanner.svelte';

  let { chatId, palette = getPalette() } = $props();

  let messages = $derived(getMessages(chatId));
  let messagesInnerEl: HTMLElement | null = null;
  let autoFollowMessages = true;
  let showScrollToBottom = $state(false);
  let lastRenderKey = '';
  let copiedMessageId = $state<string | null>(null);
  let copyFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
  let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
  let documentBatchGeneration = 0;

  // ── Enfoque desde el buscador global ─────────────────────
  // El historial debe estar cargado antes de resolver un objetivo de scroll;
  // si el mensaje no está en la copia en RAM (p. ej. llegó por WebSocket en
  // esta sesión), se recarga el historial autoritativo una vez.
  let readyChatId = $state<string | null>(null);
  let highlightMessageId = $state<number | null>(null);
  let highlightTimer: ReturnType<typeof setTimeout> | null = null;
  let focusGeneration = 0;

  let route = $derived(getRoute());

  // ── Base documental (panel de documentos del chat) ───────
  // Referencia a la instancia de DocumentPanel para enrutar aquí los
  // soltados masivos (> 3 archivos) que llegan por el DropZone global.
  type DocumentPanelApi = {
    addContextFiles: (files: File[]) => Promise<void>;
  };
  let documentPanelRef = $state<DocumentPanelApi | null>(null);

  function handleDroppedFiles(files: File[]) {
    if (files.length === 0) return;
    if (files.length > MAX_CHAT_MESSAGE_DOCUMENTS && documentPanelRef) {
      void documentPanelRef.addContextFiles(files);
      return;
    }
    handleSendFiles(files, '');
  }

  // ── Panel de fuentes (tercio derecho) ────────────────────
  let sourcesPanelOpen = $state(false);
  let sourcesPanelMsgId = $state<string | null>(null);

  let panelSources = $derived(
    sourcesPanelMsgId
      ? (messages?.find((m) => m.id === sourcesPanelMsgId)?.sources ?? [])
      : []
  );

  function toggleSourcesPanel(msgId: string) {
    if (sourcesPanelOpen && sourcesPanelMsgId === msgId) {
      closeSourcesPanel();
    } else {
      sourcesPanelMsgId = msgId;
      sourcesPanelOpen = true;
    }
  }

  function closeSourcesPanel() {
    sourcesPanelOpen = false;
    sourcesPanelMsgId = null;
  }

  // Cerrar el panel al cambiar de chat
  $effect(() => {
    chatId;
    closeSourcesPanel();
  });

  // busy local es derivado del store global por chat
  let busy = $derived(!!busyByChat[chatId]);

  // ── Nivel de análisis (thinking effort) ──────────────────────
  // El catálogo y el default global vienen del backend; la preferencia del
  // chat se guarda vía REST al elegirla. El cambio aplica al siguiente
  // mensaje, por eso el selector sigue habilitado con `busy`.
  let effortOptions = $derived(getEffortOptions());
  let effectiveEffort = $derived(getEffectiveEffort(chatId));

  function handleEffortChange(value: string) {
    void setChatReasoningEffort(chatId, value).catch(() => {
      showGlobalError(
        'No pudimos guardar tu nivel de análisis. Revisa tu conexión e intenta de nuevo.'
      );
    });
  }

  // ── Aviso fijo de espera (análisis profundo) ──────────────────
  // El banner se ancla al TRIGGER REAL del backend: el status "Esto puede
  // tardar entre 5 y 20 minutos o más..." que se emite cuando arranca un
  // subagente de análisis profundo (orchestrator_runner.py). Apenas llega,
  // aparece fijo sobre el input con un cronómetro que corre desde ese
  // instante. Se limpia al cerrar el turno (removeStatusMessages /
  // setChatBusy(false) en el store), por lo que no requiere umbral de tiempo.
  const DEEP_ANALYSIS_NOTICE_RE = /puede tardar entre \d+ y \d+ minutos/i;
  let waitElapsedMs = $state(0);

  let deepWaitStartedAt = $derived(busy ? (deepWaitByChat[chatId] ?? null) : null);
  let waitBannerVisible = $derived(deepWaitStartedAt !== null);

  function isDeepAnalysisNotice(text: string): boolean {
    return DEEP_ANALYSIS_NOTICE_RE.test(text);
  }

  function formatElapsed(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const ss = String(seconds).padStart(2, '0');
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
    }
    return `${minutes}:${ss}`;
  }

  $effect(() => {
    // Cronómetro: corre mientras el banner está visible y se detiene solo
    // cuando el aviso se desarma (fin del turno, stop, error o cambio de chat).
    const startTs = deepWaitStartedAt;
    if (startTs === null) {
      waitElapsedMs = 0;
      return;
    }
    const tick = () => {
      waitElapsedMs = Math.max(0, Date.now() - startTs);
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  });

  function clearRecoveryTimer() {
    if (recoveryTimer) {
      clearTimeout(recoveryTimer);
      recoveryTimer = null;
    }
  }

  function armRecoveryTimer(targetChatId: string) {
    clearRecoveryTimer();
    recoveryTimer = setTimeout(() => {
      if (!busyByChat[targetChatId]) return;
      initGlobalSocket();
      void pollPending(targetChatId).catch((error) => {
        console.warn('No fue posible consultar la respuesta pendiente', error);
      });
      // The backend persists the assistant response before WebSocket delivery.
      // If the socket frame was lost and the in-memory registry is already gone,
      // recover it from the REST history without requiring a page refresh.
      setTimeout(() => {
        void recoverLatestAssistantMessage(targetChatId)
          .then((recovered) => {
            if (recovered) {
              removeStatusMessages(targetChatId);
              setChatBusy(targetChatId, false);
              clearRecoveryTimer();
            } else if (busyByChat[targetChatId]) {
              armRecoveryTimer(targetChatId);
            }
          })
          .catch(() => {
            if (busyByChat[targetChatId]) armRecoveryTimer(targetChatId);
          });
      }, 2500);
    }, 25000);
  }

  function handleSendFailure(targetChatId: string, error: unknown) {
    console.error('No fue posible enviar el mensaje', error);
    removeStatusMessages(targetChatId);
    setChatBusy(targetChatId, false);
    clearRecoveryTimer();
    showGlobalError('Se perdió la conexión. Intenta enviar el mensaje nuevamente.');
  }

  async function prepareEffortForSend(targetChatId: string): Promise<boolean> {
    const generation = documentBatchGeneration;
    try {
      await awaitPendingEffortWrite(targetChatId);
      // Detener mientras se guarda no debe enviar el turno después.
      return generation === documentBatchGeneration && !!busyByChat[targetChatId];
    } catch {
      removeStatusMessages(targetChatId);
      setChatBusy(targetChatId, false);
      clearRecoveryTimer();
      showGlobalError('No enviamos el mensaje porque no se pudo guardar el nivel de análisis. Elige el nivel e intenta de nuevo.');
      return false;
    }
  }

  async function initHistory(targetChatId: string) {
    try {
      markChatRead(targetChatId);
      initGlobalSocket();
      // Catálogo de niveles + preferencia del chat (no bloquean el historial).
      void loadAIPreferences();
      void loadChatPreferences(targetChatId);
      await loadChatMessages(targetChatId);
      // Al navegar rápido, una carga anterior no debe habilitar el scroll
      // de una búsqueda del chat actualmente abierto.
      if (chatId !== targetChatId) return;
      readyChatId = targetChatId;
      void scrollMessagesToBottom(true);

      const pending = consumePendingInitialMessage(targetChatId);
      if (pending) {
        if (pending.attachmentType === 'file' && pending.files?.length) {
          void handleSendFiles(pending.files, pending.text);
          return;
        }
        setChatBusy(targetChatId, true);
        armRecoveryTimer(targetChatId);
        // Un cambio de nivel de análisis pendiente debe aterrizar antes del
        // primer turno (los chats creados desde Inicio ya lo traen en el POST).
        if (!await prepareEffortForSend(targetChatId)) return;

        if (pending.attachmentType === 'audio') {
          addUserAttachmentMessage(targetChatId, pending.text, {
            mimeType: pending.mimeType || 'audio/webm',
            mediaType: 'audio',
          });
          void sendAudioMessage(targetChatId, pending.mediaData!, pending.mimeType!)
            .catch((error) => handleSendFailure(targetChatId, error));
        } else if (pending.attachmentType === 'file') {
          const mediaType = (pending.mimeType?.startsWith('image/') ? 'image' : 'document') as 'image' | 'document';
          addUserAttachmentMessage(targetChatId, pending.text, {
            mimeType: pending.mimeType || 'application/octet-stream',
            filename: pending.filename,
            mediaType,
          });
          if (mediaType === 'image') {
            void sendImageMessage(targetChatId, pending.mediaData!, pending.mimeType!, pending.text)
              .catch((error) => handleSendFailure(targetChatId, error));
          } else {
            void sendDocumentMessage(targetChatId, pending.mediaData!, pending.mimeType!, pending.filename, pending.text)
              .catch((error) => handleSendFailure(targetChatId, error));
          }
        } else {
          // attachmentType === 'text' (comportamiento original)
          addUserMessage(targetChatId, pending.text);
          void sendChatMessage(targetChatId, pending.text)
            .catch((error) => handleSendFailure(targetChatId, error));
        }
      } else {
        // A page refresh clears Svelte's in-memory busy/status state, but the
        // Django background task keeps running. Ask the server to reattach us
        // or deliver a result that completed while this page was reloading.
        void pollPending(chatId).catch((error) => {
          console.warn('No fue posible comprobar consultas pendientes', error);
        });
      }
    } catch (e) {
      console.error(e);
      showGlobalError('No fue posible cargar los mensajes de este chat.');
      navigate('/');
    }
  }

  // ── Send text ────────────────────────────────────────────
  async function handleSendText(text: string) {
    const targetChatId = chatId;
    void scrollMessagesToBottom(true);
    // Turno nuevo estando libre: cualquier marca de espera residual
    // (p. ej. hidratada desde sessionStorage tras un refresh) ya no aplica;
    // si este turno dispara un análisis profundo, el trigger la re-arma.
    if (!busy) clearDeepWait(chatId);
    addUserMessage(chatId, text);
    touchChat(chatId);
    setChatBusy(chatId, true);
    armRecoveryTimer(chatId);
    initGlobalSocket();
    // El nivel de análisis se persiste por REST: esperamos a que el PATCH
    // aterrice para que este turno ya use el valor recién elegido.
    if (!await prepareEffortForSend(targetChatId)) return;
    void sendChatMessage(targetChatId, text).catch((error) => handleSendFailure(targetChatId, error));
  }

  // ── Send audio (voice note) ──────────────────────────────
  async function handleSendAudio(audioData: string, mimeType: string) {
    const targetChatId = chatId;
    if (busy) return;
    clearDeepWait(chatId); // turno nuevo: marca residual fuera
    void scrollMessagesToBottom(true);
    // Show audio attachment in chat
    addUserAttachmentMessage(chatId, '🎙️ Mensaje de voz', {
      mimeType,
      mediaType: 'audio',
    });
    touchChat(chatId);
    setChatBusy(chatId, true);
    armRecoveryTimer(chatId);
    initGlobalSocket();
    if (!await prepareEffortForSend(targetChatId)) return;
    void sendAudioMessage(targetChatId, audioData, mimeType).catch((error) => handleSendFailure(targetChatId, error));
  }

  // ── Send file / image ────────────────────────────────────
  async function handleSendFile(fileData: string, mimeType: string, filename: string, caption: string) {
    const targetChatId = chatId;
    if (busy) return;
    clearDeepWait(chatId); // turno nuevo: marca residual fuera
    void scrollMessagesToBottom(true);
    const mediaType = mimeType.startsWith('image/') ? 'image' as const : 'document' as const;
    const displayText = caption || filename;

    addUserAttachmentMessage(chatId, displayText, {
      mimeType,
      filename,
      mediaType,
    });
    touchChat(chatId);
    setChatBusy(chatId, true);
    armRecoveryTimer(chatId);
    initGlobalSocket();
    if (!await prepareEffortForSend(targetChatId)) return;

    if (mediaType === 'image') {
      void sendImageMessage(targetChatId, fileData, mimeType, caption).catch((error) => handleSendFailure(targetChatId, error));
    } else {
      void sendDocumentMessage(targetChatId, fileData, mimeType, filename, caption).catch((error) => handleSendFailure(targetChatId, error));
    }
  }

  async function handleSendFiles(files: File[], caption: string) {
    const targetChatId = chatId;
    if (busy || files.length === 0) return;
    clearDeepWait(chatId); // turno nuevo: marca residual fuera
    void scrollMessagesToBottom(true);
    const generation = ++documentBatchGeneration;
    const names = files.map(file => file.name);
    const displayText = caption || names.join(', ');
    addUserAttachmentMessage(chatId, displayText, {
      mimeType: files[0].type,
      filename: names.join(', '),
      mediaType: files[0].type.startsWith('image/') ? 'image' : 'document',
    });
    touchChat(chatId);
    setChatBusy(chatId, true);
    setStatusMessage(chatId, `☁️ Subiendo ${files.length} ${files.length === 1 ? 'documento' : 'documentos'} de forma segura...`);

    try {
      const uploaded = await uploadChatDocumentBatch(targetChatId, files, 'message');
      if (generation !== documentBatchGeneration) return;
      if (uploaded.failedFiles.length > 0) {
        showGlobalError(`No se pudieron subir: ${uploaded.failedFiles.join(', ')}`);
      }
      if (uploaded.documents.length === 0) {
        throw new Error('No se pudo subir ningún documento.');
      }

      setStatusMessage(targetChatId, '📄 Documentos en cola para OCR...');
      const processed = await waitForChatDocuments(
        targetChatId,
        uploaded.documents.map(doc => doc.id),
      );
      if (generation !== documentBatchGeneration) return;
      const completed = processed.filter(doc => doc.status === 'completed');
      const failed = processed.filter(doc => doc.status === 'failed');
      if (failed.length > 0) {
        showGlobalError(`No se pudieron procesar: ${failed.map(doc => doc.filename).join(', ')}`);
      }
      if (completed.length === 0) {
        throw new Error('Ningún documento pudo ser procesado.');
      }

      setStatusMessage(targetChatId, '🧠 Preparando el análisis conjunto...');
      initGlobalSocket();
      if (!await prepareEffortForSend(targetChatId)) return;
      await sendDocumentBatch(targetChatId, completed.map(doc => doc.id), caption);
      armRecoveryTimer(targetChatId);
    } catch (error: any) {
      console.error('Document batch failed', error);
      removeStatusMessages(targetChatId);
      setChatBusy(targetChatId, false);
      showGlobalError(error?.message || 'No fue posible procesar los documentos.');
    }
  }

  // ── Stop / Interrupt ─────────────────────────────────────
  function handleStop() {
    documentBatchGeneration++;
    initGlobalSocket();
    sendInterrupt(chatId);
    setChatBusy(chatId, false);
    clearRecoveryTimer();
  }

  // ── Paste from clipboard ─────────────────────────────────
  function handlePasteFile(fileData: string, mimeType: string, filename: string) {
    handleSendFile(fileData, mimeType, filename, '');
  }

  // ── Copy message ──────────────────────────────────────────
  function fallbackCopyText(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (!copied) throw new Error('Copy command failed');
  }

  async function copyMessage(messageId: string, text: string) {
    if (!text) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        fallbackCopyText(text);
      }

      copiedMessageId = messageId;
      if (copyFeedbackTimer) clearTimeout(copyFeedbackTimer);
      copyFeedbackTimer = setTimeout(() => {
        if (copiedMessageId === messageId) copiedMessageId = null;
      }, 1600);
    } catch (error) {
      console.error('Unable to copy message', error);
      showGlobalError('No fue posible copiar el mensaje.');
    }
  }

  // ── Timestamp formatting ─────────────────────────────────
  function formatTimestamp(ts: number): string {
    const d = new Date(ts);
    const today = new Date();
    const isToday = d.getDate() === today.getDate() &&
                    d.getMonth() === today.getMonth() &&
                    d.getFullYear() === today.getFullYear();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    const date = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
    return `${date} ${time}`;
  }

  // ── WebSocket subscription ───────────────────────────────
  const unsubscribeMessages = subscribeMessages((msg) => {
    const { chat_id, role, text, timestamp } = msg;
    const tsNum = timestamp ? Date.parse(timestamp) : undefined;

    if (role === 'status') {
      setStatusMessage(chat_id, text, tsNum);
      // Trigger de análisis profundo: ancla el banner fijo con cronómetro
      // desde el instante en que el backend lo anunció.
      if (isDeepAnalysisNotice(text)) {
        setDeepWait(chat_id);
      }
      // Status can be the server confirming a reattachment after refresh.
      // Restore the UI processing state that was lost with the page reload.
      setChatBusy(chat_id, true);
      armRecoveryTimer(chat_id);
    } else if (role === 'transcription') {
      updateLatestAudioTranscription(chat_id, text);
    } else if (role === 'assistant') {
      removeStatusMessages(chat_id);
      addAssistantMessage(chat_id, text, tsNum, msg.sources);
      // Promueve las URLs citadas sin borrar fuentes de respuestas anteriores.
      if (msg.sources && msg.sources.length > 0) {
        addSources(chat_id, msg.sources);
      }
      // A response frame is final in this protocol. Do not depend solely on
      // the separate END frame, which can be lost during a socket transition.
      setChatBusy(chat_id, false);
      clearRecoveryTimer();
    } else if (role === 'system' && text === 'END') {
      removeStatusMessages(chat_id);
      setChatBusy(chat_id, false);
      clearRecoveryTimer();
      const current = getRoute();
      if (!(current.name === 'chat' && current.id === chat_id)) {
        markChatUnread(chat_id);
      }
    } else if (role === 'system' && text.startsWith('Error:')) {
      removeStatusMessages(chat_id);
      setChatBusy(chat_id, false);
      clearRecoveryTimer();
      showGlobalError(text.replace(/^Error:\s*/, '') || 'Ocurrió un error en la consulta.');
    }
  });

  const unsubscribeClose = subscribeClose(() => {
    if (busyByChat[chatId]) armRecoveryTimer(chatId);
  });

  const unsubscribeError = subscribeError(() => {
    if (busyByChat[chatId]) armRecoveryTimer(chatId);
  });

  // Fuentes en vivo: el agente las va descubriendo durante el streaming.
  const unsubscribeSources = subscribeSources((chat_id, sources) => {
    if (chat_id === chatId) {
      addSources(chat_id, sources);
    }
  });

  // ── Autoscroll ───────────────────────────────────────────
  function handleMessagesScroll() {
    if (!messagesInnerEl) return;
    const distanceFromBottom =
      messagesInnerEl.scrollHeight - messagesInnerEl.scrollTop - messagesInnerEl.clientHeight;
    autoFollowMessages = distanceFromBottom <= 96;
    showScrollToBottom = !autoFollowMessages;
  }

  async function scrollMessagesToBottom(force = false) {
    if (force) autoFollowMessages = true;
    await tick();
    if (!messagesInnerEl || (!force && !autoFollowMessages)) return;
    messagesInnerEl.scrollTop = messagesInnerEl.scrollHeight;
    showScrollToBottom = false;
  }

  async function handleScrollToBottomClick() {
    autoFollowMessages = true;
    showScrollToBottom = false;
    await tick();
    messagesInnerEl?.scrollTo({
      top: messagesInnerEl.scrollHeight,
      behavior: 'smooth',
    });
  }

  // ── Enfoque de una coincidencia del buscador global ──────
  async function focusMessageFromSearch(messageId: number) {
    const targetChatId = chatId;
    const generation = ++focusGeneration;
    consumeScrollTarget(targetChatId); // una sola vez por clic
    // Evita que el autoscroll al fondo pelee con el salto al mensaje.
    autoFollowMessages = false;

    let found = getMessages(targetChatId).some((message) => message.messageId === messageId);
    if (!found) {
      try {
        await reloadChatMessages(targetChatId);
        await tick();
      } catch (error) {
        console.warn('No fue posible recargar el historial para enfocar la coincidencia', error);
        return;
      }
      if (chatId !== targetChatId || generation !== focusGeneration) return;
      found = getMessages(targetChatId).some((message) => message.messageId === messageId);
      if (!found) return; // el mensaje ya no existe (chat editado/borrado)
    }

    if (chatId !== targetChatId || generation !== focusGeneration) return;
    highlightMessageId = messageId;
    if (highlightTimer) clearTimeout(highlightTimer);
    highlightTimer = setTimeout(() => {
      highlightMessageId = null;
      highlightTimer = null;
    }, 2600);

    await tick();
    if (chatId !== targetChatId || generation !== focusGeneration) return;
    const target = messagesInnerEl?.querySelector<HTMLElement>(
      `[data-message-id="${messageId}"]`
    );
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  $effect(() => {
    const targetId = scrollTargetByChat[chatId];
    if (targetId === undefined || readyChatId !== chatId) return;
    untrack(() => {
      void focusMessageFromSearch(targetId);
    });
  });

  $effect(() => {
    const len = messages?.length ?? 0;
    const latest = len > 0 ? messages[len - 1] : null;
    const renderKey = latest
      ? `${len}:${latest.id}:${latest.ts}:${latest.text.length}`
      : `${len}`;
    if (lastRenderKey && renderKey !== lastRenderKey && autoFollowMessages) {
      void scrollMessagesToBottom();
    }
    lastRenderKey = renderKey;
  });

  // ── Get file icon class ──────────────────────────────────
  function getFileIcon(attachment: Attachment): string {
    if (attachment.mediaType === 'audio') return 'bi-mic-fill';
    if (attachment.mediaType === 'image') return 'bi-file-image';
    return 'bi-file-earmark-pdf';
  }

  function isLatestStatus(messageId: string): boolean {
    const latest = [...(messages ?? [])]
      .reverse()
      .find((message) => message.role === 'status');
    return latest?.id === messageId;
  }

  onMount(() => {
    void scrollMessagesToBottom(true);
  });

  $effect(() => {
    chatId;
    lastRenderKey = '';
    autoFollowMessages = true;
    showScrollToBottom = false;
    readyChatId = null;
    highlightMessageId = null;
    focusGeneration++;
    // initHistory reads reactive message stores before its first await. Without
    // untrack(), every incoming status retriggers this effect, causing an
    // immediate poll_pending → status → effect loop.
    untrack(() => {
      void initHistory(chatId);
    });
  });

  $effect(() => {
    return () => {
      unsubscribeMessages();
      unsubscribeClose();
      unsubscribeError();
      unsubscribeSources();
      documentBatchGeneration++;
      if (copyFeedbackTimer) clearTimeout(copyFeedbackTimer);
      if (highlightTimer) clearTimeout(highlightTimer);
      clearRecoveryTimer();
    };
  });
</script>

<DropZone
  onFiles={handleDroppedFiles}
  {palette}
  disabled={busy}
  overlayTitle="Suelta tus archivos aquí"
  overlayHint="Hasta 3 se adjuntan al mensaje · más de 3 van a tu base documental"
>
  <div class="chat-columns">
    <div class="chat-view">
      <!-- Per‑chat document vector store panel -->
      <DocumentPanel bind:this={documentPanelRef} {chatId} {palette} />

      <div class="messages-wrapper">
      <div
        class="messages-inner scroll-y"
        bind:this={messagesInnerEl}
        onscroll={handleMessagesScroll}
        aria-live="polite"
      >
        {#if !messages || messages.length === 0}
          <div class="empty-hint">
            Aún no hay mensajes en este chat. Escribe tu primera pregunta.
          </div>
        {:else}
          {#each messages as msg (msg.id)}
            {#if msg.role === 'status'}
              <div class="msg-row status" class:latest-status={isLatestStatus(msg.id)}>
                <div class="status-bubble" class:thinking={msg.text.startsWith('🧠')}>
                  {#if isLatestStatus(msg.id)}
                    <span class="status-live-dot" aria-hidden="true"></span>
                  {:else}
                    <i class="bi bi-check2 status-complete" aria-hidden="true"></i>
                  {/if}
                  <span class="status-text" style={`color: ${palette.textSecondary};`}>
                    {msg.text}
                  </span>
                </div>
              </div>
            {:else}
              <div
                class="msg-row {msg.role === 'user' ? 'user' : 'assistant'}"
                class:search-highlight={msg.messageId !== undefined && msg.messageId === highlightMessageId}
                data-message-id={msg.messageId}
                style={msg.messageId !== undefined && msg.messageId === highlightMessageId
                  ? `--search-hl: ${palette.accent};`
                  : undefined}
              >
                <div
                  class="bubble {msg.role === 'user' ? 'bubble-user' : 'bubble-assistant'}"
                >
                  <span
                    class="bubble-text has-copy"
                    style={`background-color: ${
                      msg.role === 'user' ? palette.accent : palette.bgChatInput
                    }; color: ${
                      msg.role === 'user' ? '#ffffff' : palette.textPrimary
                    };`}
                  >
                  <!-- Attachment indicator for user messages with files -->
                  {#if msg.role === 'user' && msg.attachment}
                    <div class="attachment-tag">
                      <i class="bi {getFileIcon(msg.attachment)}"></i>
                      <span class="attachment-name">
                        {msg.attachment.filename || (msg.attachment.mediaType === 'audio' ? 'Audio' : 'Archivo')}
                      </span>
                    </div>
                  {/if}

                  {#if msg.role === 'assistant'}
                    <MarkdownContent content={msg.text} {palette} />
                  {:else}
                    <!-- Show text only if it's not redundant with attachment -->
                    {#if !msg.attachment || (msg.text && msg.text !== '🎙️ Mensaje de voz' && msg.text !== (msg.attachment.filename || ''))}
                      {#if msg.attachment?.mediaType === 'audio'}
                        <span class="transcription-label">Transcripción</span>
                      {/if}
                      <MarkdownContent content={msg.text} {palette} color="#ffffff" />
                    {/if}
                  {/if}

                  <button
                    class="copy-message-btn"
                    class:copied={copiedMessageId === msg.id}
                    type="button"
                    onclick={() => copyMessage(msg.id, msg.text)}
                    aria-label={copiedMessageId === msg.id ? 'Mensaje copiado' : 'Copiar mensaje'}
                    title={copiedMessageId === msg.id ? 'Copiado' : 'Copiar mensaje'}
                    style={`color: ${
                      msg.role === 'user' ? 'rgba(255,255,255,0.92)' : palette.textSecondary
                    };`}
                  >
                    <i class="bi {copiedMessageId === msg.id ? 'bi-check2' : 'bi-copy'}"></i>
                  </button>

                  {#if msg.ts}
                    <span
                      class="timestamp {msg.role === 'user' ? 'left' : 'right'}"
                      style={`color: ${
                        msg.role === 'user'
                          ? 'rgba(255,255,255,0.85)'
                          : palette.textSecondary
                      };`}
                    >
                      {formatTimestamp(msg.ts)}
                    </span>
                  {/if}
                </span>

                <div class="message-bottom-actions {msg.role}">
                  <button
                    class="copy-bottom-btn"
                    class:copied={copiedMessageId === msg.id}
                    type="button"
                    onclick={() => copyMessage(msg.id, msg.text)}
                    aria-label={copiedMessageId === msg.id ? 'Mensaje copiado' : 'Copiar mensaje'}
                    title={copiedMessageId === msg.id ? 'Copiado' : 'Copiar mensaje'}
                    style={`color: ${palette.textSecondary};`}
                  >
                    <i class="bi {copiedMessageId === msg.id ? 'bi-check2' : 'bi-copy'}"></i>
                    <span>{copiedMessageId === msg.id ? 'Copiado' : 'Copiar'}</span>
                  </button>

                  {#if msg.role === 'assistant' && msg.sources && msg.sources.length > 0}
                    <button
                      class="sources-bottom-btn"
                      class:active={sourcesPanelOpen && sourcesPanelMsgId === msg.id}
                      type="button"
                      onclick={() => toggleSourcesPanel(msg.id)}
                      aria-label="Ver fuentes de esta respuesta"
                      aria-pressed={sourcesPanelOpen && sourcesPanelMsgId === msg.id}
                      title="Ver fuentes de esta respuesta"
                      style={`color: ${
                        sourcesPanelOpen && sourcesPanelMsgId === msg.id
                          ? palette.accent
                          : palette.textSecondary
                      };`}
                    >
                      <i class="bi bi-link-45deg"></i>
                      <span>Fuentes</span>
                    </button>
                  {/if}
                </div>
              </div>
            </div>
          {/if}
        {/each}
      {/if}
    </div>
    {#if showScrollToBottom}
      <button
        class="scroll-to-bottom"
        type="button"
        onclick={handleScrollToBottomClick}
        aria-label="Ir al final de la conversación"
        title="Ir al final"
        style={`background-color: ${palette.bgChatInput}; color: ${palette.textSecondary}; border-color: ${palette.borderSoft};`}
      >
        <i class="bi bi-arrow-down"></i>
      </button>
    {/if}
  </div>

  <div class="input-area">
    {#if waitBannerVisible}
      <div
        class="wait-banner"
        role="status"
        aria-live="polite"
        style={`background-color: ${palette.bgChatInput}; border-color: ${palette.borderSoft}; color: ${palette.textSecondary};`}
      >
        <i class="bi bi-hourglass-split wait-banner-icon" style={`color: ${palette.accent};`} aria-hidden="true"></i>
        <span class="wait-banner-time" style={`color: ${palette.accent};`} aria-hidden="true">{formatElapsed(waitElapsedMs)}</span>
        <span class="wait-banner-text">
          Análisis profundo en curso: puede tardar entre 5 y 20 minutos o más.
          No necesitas reenviar tu mensaje, te avisamos apenas esté listo.
        </span>
      </div>
    {/if}
    <ChatInput
      {palette}
      {busy}
      effort={effectiveEffort}
      {effortOptions}
      onEffortChange={handleEffortChange}
      onStop={handleStop}
      onSend={handleSendText}
      onSendAudio={handleSendAudio}
      onSendFiles={handleSendFiles}
    />
  </div>
  </div>

  {#if sourcesPanelOpen}
    {#key sourcesPanelMsgId}
      <SourcesPanel
        sources={panelSources}
        {palette}
        title="Fuentes de esta respuesta"
        onClose={closeSourcesPanel}
      />
    {/key}
  {/if}
  </div>
</DropZone>

<style>
  .chat-columns {
    flex: 1;
    display: flex;
    min-height: 0;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    position: relative; /* ancla para el overlay de fuentes en móvil */
    overflow: hidden;
  }

  .chat-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .messages-wrapper {
    flex: 1;
    display: flex;
    justify-content: center;
    padding: 12px 0;
    min-height: 0;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    position: relative;
    overflow: hidden;
  }

  .scroll-to-bottom {
    position: absolute;
    left: 50%;
    bottom: 20px;
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    transform: translateX(-50%);
    border: 1px solid;
    border-radius: 999px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
    opacity: 0.72;
    cursor: pointer;
    z-index: 2;
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  .scroll-to-bottom:hover,
  .scroll-to-bottom:focus-visible {
    opacity: 1;
    transform: translateX(-50%) translateY(-1px);
  }

  .messages-inner {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    max-width: 720px;
    padding: 0 32px;
    overflow-y: auto;
    min-height: 0;
    min-width: 0;
    max-width: min(720px, 100%);
    box-sizing: border-box;
    overflow-x: hidden;
  }

  .msg-row {
    display: flex;
    margin-bottom: 8px;
    min-width: 0;
    max-width: 100%;
  }

  .msg-row.user {
    justify-content: flex-end;
  }

  .msg-row.assistant {
    justify-content: flex-start;
  }

  /* Coincidencia del buscador global: anillo + destello de enfoque. */
  .msg-row.search-highlight .bubble {
    outline: 2px solid var(--search-hl, #f0b429);
    outline-offset: 2px;
    animation: search-hit 0.85s ease-out 2;
  }

  @keyframes search-hit {
    0% {
      box-shadow: 0 0 0 7px color-mix(in srgb, var(--search-hl, #f0b429) 32%, transparent);
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }

  .msg-row.status {
    justify-content: center;
    margin-bottom: 2px;
    opacity: 0.48;
    transform: scale(0.985);
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .msg-row.status.latest-status {
    opacity: 1;
    transform: scale(1);
    margin-bottom: 7px;
  }

  .status-bubble {
    display: flex;
    align-items: center;
    gap: 7px;
    max-width: 86%;
    padding: 3px 8px;
    text-align: left;
  }

  .status-text {
    font-size: 11.5px;
    line-height: 1.35;
  }

  .latest-status .status-text {
    font-size: 12px;
    font-weight: 500;
  }

  .status-complete {
    flex: 0 0 auto;
    color: #22c55e;
    font-size: 12px;
  }

  /* Thinking en vivo (🧠): itálica atenuada, se reemplaza in-place.
     Sin line-clamp: la burbuja crece con el pensamiento acumulado. */
  .status-bubble.thinking .status-text {
    font-style: italic;
    font-weight: 400;
    opacity: 0.75;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .status-bubble.thinking .status-live-dot {
    animation: thinkingPulse 1.4s ease-in-out infinite;
  }

  @keyframes thinkingPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.35; transform: scale(0.8); }
  }

  @media (prefers-reduced-motion: reduce) {
    .status-bubble.thinking .status-live-dot {
      animation: none;
    }
  }

  .status-live-dot {
    width: 7px;
    height: 7px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #3b82f6;
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
    animation: status-pulse 1.6s ease-out infinite;
  }

  @keyframes status-pulse {
    0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.42); }
    65%, 100% { box-shadow: 0 0 0 7px rgba(59, 130, 246, 0); }
  }

  .bubble {
    max-width: 80%;
    min-width: 0;
    font-size: 14px;
    line-height: 1.4;
  }

  .bubble-user {
    border-bottom-right-radius: 4px;
  }

  .bubble-assistant {
    border-bottom-left-radius: 4px;
  }

  .bubble-text {
    display: inline-block;
    min-width: 0;
    max-width: 100%;
    padding: 8px 12px 14px 12px;
    border-radius: 16px;
    position: relative;
    transition: background-color 0.15s ease;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .bubble-text.has-copy {
    padding-right: 38px;
  }

  .copy-message-btn {
    position: absolute;
    top: 5px;
    right: 6px;
    width: 26px;
    height: 26px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 7px;
    background: transparent;
    opacity: 0;
    cursor: pointer;
    transition: opacity 0.15s ease, background-color 0.15s ease, transform 0.15s ease;
  }

  .bubble-text:hover .copy-message-btn,
  .copy-message-btn:focus-visible,
  .copy-message-btn.copied {
    opacity: 1;
  }

  .copy-message-btn:hover {
    background: rgba(128, 128, 128, 0.17);
    transform: translateY(-1px);
  }

  .copy-message-btn.copied {
    color: #22c55e !important;
  }

  .message-bottom-actions {
    display: flex;
    margin-top: 3px;
    padding: 0 4px;
  }

  .message-bottom-actions.user {
    justify-content: flex-end;
  }

  .message-bottom-actions.assistant {
    justify-content: flex-start;
  }

  .copy-bottom-btn,
  .sources-bottom-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    padding: 3px 6px;
    font-size: 10.5px;
    opacity: 0.58;
    cursor: pointer;
    transition: opacity 0.15s ease, background-color 0.15s ease;
  }

  .copy-bottom-btn:hover,
  .copy-bottom-btn:focus-visible,
  .copy-bottom-btn.copied,
  .sources-bottom-btn:hover,
  .sources-bottom-btn:focus-visible,
  .sources-bottom-btn.active {
    opacity: 1;
    background: rgba(128, 128, 128, 0.1);
  }

  .copy-bottom-btn.copied {
    color: #22c55e !important;
  }

  /* ── Attachment tag inside user bubble ──────────────── */
  .attachment-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    padding: 4px 8px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    font-size: 12px;
  }

  .attachment-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
    min-width: 0;
  }

  .transcription-label {
    display: block;
    margin: 5px 0 1px;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.72;
  }

  /* ── Timestamp ──────────────────────────────────────── */
  .timestamp {
    position: absolute;
    bottom: 4px;
    font-size: 10px;
    opacity: 0;
    transform: translateY(4px);
    transition:
      opacity 0.18s ease,
      transform 0.18s ease;
    pointer-events: none;
  }

  .timestamp.left {
    left: 10px;
    text-align: left;
  }

  .timestamp.right {
    right: 10px;
    text-align: right;
  }

  .bubble-text:hover .timestamp {
    opacity: 1;
    transform: translateY(0);
  }

  .empty-hint {
    text-align: center;
    margin-top: 40px;
    opacity: 0.8;
    font-size: 13px;
  }

  .input-area {
    padding: 0 32px 20px 32px;
    flex: 0 0 auto;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  /* ── Aviso fijo de espera (turnos largos) ───────────── */
  /* No forma parte del scroll: vive entre la lista de mensajes y el input,
     por lo que permanece visible todo el tiempo mientras el turno corre. */
  .wait-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 720px;
    margin: 0 auto 8px auto;
    padding: 7px 12px;
    box-sizing: border-box;
    border: 1px solid;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.35;
    animation: waitBannerIn 0.22s ease-out;
  }

  .wait-banner-icon {
    flex: 0 0 auto;
    font-size: 13px;
  }

  .wait-banner-time {
    flex: 0 0 auto;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .wait-banner-text {
    min-width: 0;
  }

  @keyframes waitBannerIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .wait-banner {
      animation: none;
    }
  }

  .messages-inner::-webkit-scrollbar {
    width: 6px;
  }
  .messages-inner::-webkit-scrollbar-track {
    background: transparent;
  }
  .messages-inner::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.18);
    border-radius: 3px;
  }
  .messages-inner {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
  }

  @media (max-width: 768px) {
    .messages-inner {
      max-width: 100%;
      padding: 0 12px;
    }
    .bubble {
      max-width: 90%;
    }
    .input-area {
      padding: 0 12px max(14px, env(safe-area-inset-bottom)) 12px;
    }
    .copy-message-btn {
      opacity: 0.65;
    }
  }
</style>
